import { Component, computed, inject, input, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import {
  FormSchemaDataDto,
  FormSchemaDetailedDto,
  FormSchemaUpdateDto,
  FormTemplateClient,
  FormTypeMiniDto,
  FormTypesClient,
  TransitionRequestDto,
} from '@unity/clients';
import { DropdownWorkflowControlComponent } from '@unity/forms/dropdown-workflow-control/dropdown-workflow-control.component';
import { SurveyCreatorControlComponent } from '@unity/forms/survey-creator-control/survey-creator-control.component';
import { TextareaControlComponent } from '@unity/forms/textarea-control/textarea-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { defaultObjectFormControl, numericFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';

class FormTemplateUpdateSchema implements FormGroupControlSchema<FormSchemaUpdateDto> {
  public name: FormControl<string>;
  public description: FormControl<string>;
  public default_workflow_id: FormControl<number>;
  public current_state_id: FormControl<number>;
  public data: FormControl<FormSchemaDataDto>;
  public type_id: FormControl<number>;

  public constructor(template?: FormSchemaDetailedDto) {
    this.name = stringFormControl({ disabled: true, value: template?.name });
    this.description = stringFormControl({ disabled: true, value: template?.description });
    this.default_workflow_id = numericFormControl({ disabled: true, value: template?.default_workflow.id });
    this.current_state_id = numericFormControl({ disabled: true, value: template?.current_state.id });
    this.data = defaultObjectFormControl<FormSchemaDataDto>({ disabled: true, value: template?.data });
    this.type_id = numericFormControl({ disabled: true, value: template?.type.id });
  }
}
@Component({
  selector: 'unity-manage-form-template-page',
  imports: [
    PageContainerComponent,
    PanelManageSharedComponent,
    FormLayoutComponent,
    TextboxControlComponent,
    TextareaControlComponent,
    SurveyCreatorControlComponent,
    DropdownWorkflowControlComponent,
    ActionSharedComponent,
    WorkflowSelectorSharedComponent,
  ],
  templateUrl: './manage-form-template-page.component.html',
  styleUrl: './manage-form-template-page.component.scss',
})
export class ManageFormTemplatePageComponent extends AbstractManageComponent<
  FormSchemaDetailedDto,
  FormSchemaUpdateDto,
  FormTemplateUpdateSchema
> {
  private readonly formTemplateClient = inject(FormTemplateClient);
  private readonly formTypesClient = inject(FormTypesClient);

  public readonly $formSchemaId = input.required({ alias: 'formSchemaId', transform: (value: string) => parseInt(value) });

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      return resourceId ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.formTemplateClient.getOneAsDetailed(resourceId),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<FormTemplateUpdateSchema>) =>
    this.formTemplateClient.updateOne(resourceId, form.getRawValue());

  protected override deleteResourceFn$ = (resourceId: number) => this.formTemplateClient.deleteOne(resourceId);

  protected override newControlsFn(dto?: FormSchemaDetailedDto) {
    return new FormTemplateUpdateSchema(dto);
  }

  protected override availableTransitionsFn$ = (_resourceId: number) =>
    this.availableTransitionsClient.getAllForFormSchemaAsMini(_resourceId);
  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) =>
    this.transitionsClient.setForFormTemplate(resourceId, transition);

  protected formTypesResource = rxResource({
    stream: () => this.formTypesClient.getAllAsMini(),
  });

  protected readonly $selectableFormTypes: Signal<FormTypeMiniDto[]> = computed(() => this.formTypesResource.value()?.data ?? []);

  protected override editEntitlement: UnityEntitlementPath = 'FormSchema:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'FormSchema:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'FormSchema:Delete';
  protected override resourceName = 'Form Schema';
  protected override $resourceId = this.$formSchemaId;
  protected override readonly $contentHeader = computed(() => `Form Schema: ${this.$resource()?.name}`);
  protected returnToDashboardAction = QuickActions.ReturnToDashboard;
  protected readonly $cloneFormTemplateAction = computed(() => QuickActions.CloneFormTemplate(this.$formSchemaId()));
}
